/**
 * Single source of truth for rendering checkpoint slot labels (2PM / 5PM /
 * 8PM by default, but driven by the actual configured store hours so a
 * 9:30 PM close shows up as "9:30PM" instead of "8PM").
 *
 * Created 2026-02-14 after the test agent flagged that ManagerDashboard's
 * `tabLabels` (CheckpointEntry) and `cpLabels` (GoalCalculator) duplicated
 * the same formatHM mapping. Future store-hour additions / changes should
 * only touch this file.
 *
 * Background:
 *   - Backend stores chain hours as `cp1_time` / `cp2_time` / `cp3_time`
 *     in HH:MM (24h) format on the global settings doc.
 *   - Per-weekday overrides live in `settings.day_hours_overrides[day]`
 *     where `day` is `sun|mon|tue|wed|thu|fri|sat`. Override fields use
 *     the same `cp1_time` / `cp2_time` / `cp3_time` keys.
 *   - The slot keys themselves (`"2pm"` / `"5pm"` / `"8pm"`) are INTERNAL
 *     API identifiers and never change — they're only the storage bucket.
 *     The display label is what we surface to the user.
 */

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/** Format `HH:MM` 24h → `H:MM AM/PM`. Returns the raw input if it can't
 *  parse so a partial / dirty setting never breaks render. */
export function formatCheckpointTime(str) {
  if (!str || typeof str !== "string") return str || "";
  const m = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return str;
  const h = parseInt(m[1], 10);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return min === "00" ? `${h12} ${ampm}` : `${h12}:${min} ${ampm}`;
}

/** Pull today's effective checkpoint times from settings, honoring any
 *  per-weekday override AND any per-store override surfaced by the
 *  backend (`settings.hours_override.day_hours_overrides[day]` or
 *  top-level `settings.hours_override.cp{1,2,3}_time`). Optionally pass
 *  `dateStr` (YYYY-MM-DD) to pick a different day; falls back to today.
 *
 *  Priority order (highest → lowest):
 *    1. settings.hours_override.day_hours_overrides[<weekday>].cp{n}_time
 *    2. settings.hours_override.cp{n}_time
 *    3. settings.day_hours_overrides[<weekday>].cp{n}_time
 *    4. settings.cp{n}_time
 *    5. Hard default ("14:00" / "17:00" / "20:00")
 *
 *  This mirrors the backend `effective_hours_for_date()` exactly so the
 *  Zone Sheet UI, printed PDF, Manager Dashboard CheckpointEntry,
 *  reminders, and EOD email all surface the SAME label for a given
 *  store/date. (User ask 2026-06-16 Phase C — "checkpoint hours should
 *  honor store hours / per-store overrides across all surfaces".)
 *
 *  Returns `{ cp1_time, cp2_time, cp3_time }` in HH:MM 24h.
 */
export function getEffectiveCheckpointTimes(settings, dateStr) {
  const s = settings || {};
  const so = s.hours_override || {};
  let dayKey;
  if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Construct the date in LOCAL time so day-of-week matches the manager's
    // calendar (not UTC). `new Date("2026-02-15")` parses as UTC midnight
    // which can shift to Saturday in MST. Splitting components dodges that.
    const [y, m, d] = dateStr.split("-").map(Number);
    dayKey = DAY_KEYS[new Date(y, m - 1, d).getDay()];
  } else {
    dayKey = DAY_KEYS[new Date().getDay()];
  }
  const storeDayOvr = (so.day_hours_overrides || {})[dayKey] || {};
  const chainDayOvr = (s.day_hours_overrides || {})[dayKey] || {};
  const pick = (key, hardDefault) =>
    storeDayOvr[key] ||
    so[key] ||
    chainDayOvr[key] ||
    s[key] ||
    hardDefault;
  return {
    cp1_time: pick("cp1_time", "14:00"),
    cp2_time: pick("cp2_time", "17:00"),
    cp3_time: pick("cp3_time", "20:00"),
  };
}

/** Returns the per-slot display label map keyed by internal slot id.
 *  Used by both the checkpoint tab strip and the Goal Calculator pills.
 *
 *  Example: with `cp3_time = "21:30"`, returns
 *    { "2pm": "2PM", "5pm": "5PM", "8pm": "9:30PM" }
 */
export function getCheckpointLabels(settings, dateStr) {
  const eff = getEffectiveCheckpointTimes(settings, dateStr);
  const lbl = (t) => formatCheckpointTime(t).replace(/\s/g, "").toUpperCase();
  return {
    "2pm": lbl(eff.cp1_time),
    "5pm": lbl(eff.cp2_time),
    "8pm": lbl(eff.cp3_time),
  };
}
